<template>
  <div class="dcb-step-breadcrumb">
    <a
      id="dcb-instructions"
      style="position: absolute; top: -80px"
    />
    <Heading
      tag="h2"
      class="dcb-step-breadcrumb__title text--center"
      size="sm"
      spaces-bottom
    >
      <template v-if="isMailSent">
        App-Käufe und In-App-Käufe auf Handyrechnung? So geht's ganz einfach
      </template>
      <template v-else>
        {{ heading }}
      </template>
    </Heading>

    <Paragraph align="center">
      Vor dem Kauf die Zahlungsmethode „Handyrechnung“ im App Store oder Play Store hinzufügen und
      an die oberste Stelle setzen.
    </Paragraph>

    <div class="dcb-step-breadcrumb__list">
      <div class="dcb-step-breadcrumb__entry-container">
        <div class="dcb-step-breadcrumb__entry">
          <div class="dcb-step-breadcrumb__indicator">1.</div>
          <div class="dcb-step-breadcrumb__entry-title">
            <template v-if="isMobile"> Link klicken </template>
            <template v-else> QR-Code scannen </template>
          </div>
        </div>
      </div>

      <div class="dcb-step-breadcrumb__entry-container">
        <div class="dcb-step-breadcrumb__entry">
          <div class="dcb-step-breadcrumb__indicator">2.</div>
          <div class="dcb-step-breadcrumb__entry-title">
            Zahlungsmethode „Handyrechnung“ auswählen
          </div>
        </div>
      </div>

      <div class="dcb-step-breadcrumb__entry-container">
        <div class="dcb-step-breadcrumb__entry">
          <div class="dcb-step-breadcrumb__indicator">3.</div>
          <div class="dcb-step-breadcrumb__entry-title">Produkt aussuchen und bezahlen</div>
        </div>
      </div>
    </div>

    <div class="dcb-step-breadcrumb__entry-qr">
      <template v-if="device === 'Desktop'">
        <div class="dcb-step-breadcrumb__qr-code">
          <QrCodeAppStore />
        </div>
        <div class="dcb-step-breadcrumb__qr-code">
          <QrCodeGooglePlay />
        </div>
      </template>
      <LinkButton
        v-else
        tag="a"
        primary
        target="_blank"
        :to="device === 'iOS' ? appStore : googlePlay"
      >
        <strong>Jetzt einrichten</strong>
      </LinkButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { detectDevice, Heading, LinkButton, Paragraph } from '@i22team/td-longtail-vue-components';
import QrCodeAppStore from '~/components/dcb/qr-code-app-store.vue';
import QrCodeGooglePlay from '~/components/dcb/qr-code-google-play.vue';

import { googlePlay, appStore } from './qr-code-links';

export interface DCBStepBreadcrumbProps {
  isMailSent?: boolean;
  heading?: string;
}
withDefaults(defineProps<DCBStepBreadcrumbProps>(), {
  heading: '',
});

const device = detectDevice.useDetectDevice();

const isMobile = computed(() => device.value !== 'Desktop');
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.dcb-step-breadcrumb {
  margin-bottom: 3rem;
  position: relative;

  &__list {
    display: flex;
  }

  &__entry-container {
    flex: 0 1 33%;
    scroll-snap-align: start;

    @include mq($mq-sm) {
      flex-basis: 50%;
    }

    @include mq($mq-md) {
      flex-basis: calc(100% / 3);
    }

    &:first-child {
      .dcb-step-breadcrumb__indicator:before {
        display: none;
      }
    }

    &:last-child {
      .dcb-step-breadcrumb__indicator:after {
        display: none;
      }
    }
  }

  &__entry {
    height: calc(100% - $grid-base * 2);
    padding: $grid-base * 0.5;
    display: grid;
    grid-template-rows: $grid-base * 3 auto;
    justify-content: center;
    position: relative;
    grid-gap: $grid-base;
    margin-bottom: 1rem;
    @include mq($mq-sm) {
      padding: $grid-base;
    }
  }

  &__entry-title {
    grid-column: 1 / 3;
    grid-row: 2;
    font-weight: 700;
    text-align: center;
    @include mq(false, $mq-sm - 1) {
      line-height: 1;
      font-size: 0.8em;
    }
  }

  &__entry-qr {
    align-self: center;
    display: flex;
    justify-content: center;
    gap: 2rem;
  }

  &__qr-code {
    text-align: left;
    margin-bottom: $grid-base;
  }

  &__indicator {
    grid-column: 1 / 3;
    grid-row: 1;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    flex-shrink: 0;
    padding-top: 1px;
    padding-left: 3px;
    color: white;
    width: $grid-base * 2;
    height: $grid-base * 2;
    background: var(--color-primary);
    border-radius: 50%;
    font-size: 18px;
    line-height: 24px;

    &:before,
    &:after {
      content: '';
      height: 1px;
      width: 50%;
      background: var(--color-primary);
      position: absolute;
      top: $grid-base * 1.5;

      @include mq($mq-sm) {
        top: $grid-base * 2;
      }
    }

    &:before {
      left: $grid-base * -1;
    }

    &:after {
      left: calc(50% + $grid-base);
    }
  }
}
</style>
